import { useId, useState } from 'react';

import { formatCurrencyCents } from '../../shared/format.ts';
import type { InvoiceListItem } from '../../shared/invoice.ts';

export interface InvoiceHistoryListProps {
  invoices: InvoiceListItem[];
  onOpen: (invoice: InvoiceListItem) => void;
  onDuplicate: (invoice: InvoiceListItem) => void;
  onDelete: (invoice: InvoiceListItem) => void;
  onDownloadPdf: (invoice: InvoiceListItem) => void;
  onPaymentStatusChange: (invoice: InvoiceListItem, isPaid: boolean) => void;
  actionBusy?: boolean;
  downloadPendingIds?: ReadonlySet<string>;
  paymentPendingIds?: ReadonlySet<string>;
}

type InvoiceActionHandlers = Pick<
  InvoiceHistoryListProps,
  'onOpen' | 'onDuplicate' | 'onDelete' | 'onDownloadPdf' | 'onPaymentStatusChange'
>;

interface InvoiceActionState {
  actionBusy?: boolean;
  downloadPending: boolean;
  paymentPending: boolean;
}

interface MobileInvoiceCardProps extends InvoiceActionHandlers, InvoiceActionState {
  invoice: InvoiceListItem;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
}

interface InvoiceRowActionsProps extends InvoiceActionHandlers, InvoiceActionState {
  invoice: InvoiceListItem;
  onActionComplete?: () => void;
  variant?: 'row' | 'menu';
}

interface InvoiceActionItem {
  label: string;
  ariaLabel: string;
  disabled?: boolean;
  tone?: 'neutral' | 'danger';
  onClick: () => void;
}

export function InvoiceHistoryList({
  invoices,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
  actionBusy,
  downloadPendingIds = new Set<string>(),
  paymentPendingIds = new Set<string>(),
}: InvoiceHistoryListProps): React.ReactElement {
  const [openMobileMenuInvoiceId, setOpenMobileMenuInvoiceId] = useState<string | null>(null);

  return (
    <>
      <DesktopInvoiceTable
        invoices={invoices}
        actionBusy={actionBusy}
        downloadPendingIds={downloadPendingIds}
        paymentPendingIds={paymentPendingIds}
        onOpen={onOpen}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDownloadPdf={onDownloadPdf}
        onPaymentStatusChange={onPaymentStatusChange}
      />

      <MobileInvoiceCards
        invoices={invoices}
        openMenuInvoiceId={openMobileMenuInvoiceId}
        actionBusy={actionBusy}
        downloadPendingIds={downloadPendingIds}
        paymentPendingIds={paymentPendingIds}
        onToggleMenu={(invoiceId) =>
          setOpenMobileMenuInvoiceId((current) => (current === invoiceId ? null : invoiceId))
        }
        onCloseMenu={() => setOpenMobileMenuInvoiceId(null)}
        onOpen={onOpen}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDownloadPdf={onDownloadPdf}
        onPaymentStatusChange={onPaymentStatusChange}
      />
    </>
  );
}

function DesktopInvoiceTable({
  invoices,
  actionBusy,
  downloadPendingIds,
  paymentPendingIds,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
}: InvoiceActionHandlers & {
  invoices: InvoiceListItem[];
  actionBusy?: boolean;
  downloadPendingIds: ReadonlySet<string>;
  paymentPendingIds: ReadonlySet<string>;
}): React.ReactElement {
  return (
    <div className="hidden rounded-2xl border border-line bg-paper-soft shadow-[0_12px_36px_-28px_rgba(31,27,21,0.45)] md:block">
      <table aria-label="Invoices" className="min-w-full text-sm">
        <DesktopInvoiceTableHead />
        <tbody className="divide-y divide-line-soft">
          {invoices.map((invoice) => (
            <DesktopInvoiceRow
              key={invoice.id}
              invoice={invoice}
              actionBusy={actionBusy}
              downloadPending={downloadPendingIds.has(invoice.id)}
              paymentPending={paymentPendingIds.has(invoice.id)}
              onOpen={onOpen}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onDownloadPdf={onDownloadPdf}
              onPaymentStatusChange={onPaymentStatusChange}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DesktopInvoiceTableHead(): React.ReactElement {
  const headers: Array<{ label: string; align: 'left' | 'right' }> = [
    { label: 'Number', align: 'left' },
    { label: 'Date', align: 'left' },
    { label: 'Currency', align: 'left' },
    { label: 'Bill To', align: 'left' },
    { label: 'Total', align: 'right' },
    { label: 'Actions', align: 'right' },
  ];

  return (
    <thead className="border-b border-line bg-paper-deep/60 text-ink-muted">
      <tr>
        {headers.map((header) => (
          <th
            key={header.label}
            scope="col"
            className={`px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.22em] ${
              header.align === 'right' ? 'text-right' : 'text-left'
            }`}
          >
            {header.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function DesktopInvoiceRow({
  invoice,
  actionBusy,
  downloadPending,
  paymentPending,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
}: InvoiceActionHandlers &
  InvoiceActionState & {
    invoice: InvoiceListItem;
  }): React.ReactElement {
  return (
    <tr className="transition hover:bg-paper-deep/40">
      <InvoiceNumberCell invoice={invoice} />
      <td className="px-5 py-4 align-top text-ink-soft">{formatInvoiceDate(invoice.date)}</td>
      <td className="px-5 py-4 align-top font-mono text-xs uppercase tracking-[0.18em] text-ink-muted">
        {invoice.currency}
      </td>
      <td className="break-words px-5 py-4 align-top text-ink-soft">{invoice.billToName ?? '—'}</td>
      <td className="whitespace-nowrap px-5 py-4 align-top text-right font-serif text-lg tabular-nums text-ink">
        {formatCurrencyCents(invoice.totalCents, invoice.currency)}
      </td>
      <td className="px-5 py-4 align-top">
        <InvoiceRowActions
          invoice={invoice}
          actionBusy={actionBusy}
          downloadPending={downloadPending}
          paymentPending={paymentPending}
          onOpen={onOpen}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onDownloadPdf={onDownloadPdf}
          onPaymentStatusChange={onPaymentStatusChange}
        />
      </td>
    </tr>
  );
}

function InvoiceNumberCell({ invoice }: { invoice: InvoiceListItem }): React.ReactElement {
  return (
    <td className="px-5 py-4 align-top">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="break-words font-serif text-lg italic leading-tight tabular-nums text-ink">
          Invoice #{invoice.number}
        </span>
        {invoice.isArchive ? <ArchiveTag /> : null}
        <PaymentBadge paid={invoice.isPaid} />
      </div>
    </td>
  );
}

function MobileInvoiceCards({
  invoices,
  openMenuInvoiceId,
  actionBusy,
  downloadPendingIds,
  paymentPendingIds,
  onToggleMenu,
  onCloseMenu,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
}: InvoiceActionHandlers & {
  invoices: InvoiceListItem[];
  openMenuInvoiceId: string | null;
  actionBusy?: boolean;
  downloadPendingIds: ReadonlySet<string>;
  paymentPendingIds: ReadonlySet<string>;
  onToggleMenu: (invoiceId: string) => void;
  onCloseMenu: () => void;
}): React.ReactElement {
  return (
    <div className="space-y-3 md:hidden">
      {invoices.map((invoice) => (
        <MobileInvoiceCard
          key={invoice.id}
          invoice={invoice}
          menuOpen={openMenuInvoiceId === invoice.id}
          actionBusy={actionBusy}
          downloadPending={downloadPendingIds.has(invoice.id)}
          paymentPending={paymentPendingIds.has(invoice.id)}
          onToggleMenu={() => onToggleMenu(invoice.id)}
          onCloseMenu={onCloseMenu}
          onOpen={onOpen}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onDownloadPdf={onDownloadPdf}
          onPaymentStatusChange={onPaymentStatusChange}
        />
      ))}
    </div>
  );
}

function MobileInvoiceCard({
  invoice,
  menuOpen,
  actionBusy,
  downloadPending,
  paymentPending,
  onToggleMenu,
  onCloseMenu,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
}: MobileInvoiceCardProps): React.ReactElement {
  const menuId = useId();

  return (
    <article
      className="rounded-2xl border border-line bg-paper-soft p-4 shadow-[0_8px_20px_-18px_rgba(31,27,21,0.45)]"
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !menuOpen) return;
        event.stopPropagation();
        onCloseMenu();
      }}
    >
      <MobileInvoiceSummary
        invoice={invoice}
        menuOpen={menuOpen}
        menuId={menuId}
        onToggleMenu={onToggleMenu}
      />
      <MobileInvoiceMenu
        invoice={invoice}
        menuOpen={menuOpen}
        menuId={menuId}
        actionBusy={actionBusy}
        downloadPending={downloadPending}
        paymentPending={paymentPending}
        onOpen={onOpen}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDownloadPdf={onDownloadPdf}
        onPaymentStatusChange={onPaymentStatusChange}
        onActionComplete={onCloseMenu}
      />
    </article>
  );
}

function MobileInvoiceSummary({
  invoice,
  menuOpen,
  menuId,
  onToggleMenu,
}: {
  invoice: InvoiceListItem;
  menuOpen: boolean;
  menuId: string;
  onToggleMenu: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="invoice-card-summary"
      className="flex min-w-0 items-start justify-between gap-3"
    >
      <div className="min-w-0 space-y-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="break-words font-serif text-lg italic leading-tight tabular-nums text-ink">
            Invoice #{invoice.number}
          </p>
          {invoice.isArchive ? <ArchiveTag /> : null}
          <PaymentBadge paid={invoice.isPaid} />
        </div>
        <p className="break-words text-sm text-ink-soft">{invoice.billToName ?? '—'}</p>
        <p className="text-xs uppercase tracking-[0.16em] text-ink-muted">
          {formatInvoiceDate(invoice.date)}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2.5">
        <p className="text-right font-serif text-xl tabular-nums leading-tight text-ink">
          {formatCurrencyCents(invoice.totalCents, invoice.currency)}
        </p>
        <button
          type="button"
          aria-label={`Invoice #${invoice.number} actions`}
          aria-haspopup="true"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={onToggleMenu}
          className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-4 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
        >
          Actions
        </button>
      </div>
    </div>
  );
}

function MobileInvoiceMenu({
  invoice,
  menuOpen,
  menuId,
  actionBusy,
  downloadPending,
  paymentPending,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
  onActionComplete,
}: InvoiceActionHandlers &
  InvoiceActionState & {
    invoice: InvoiceListItem;
    menuOpen: boolean;
    menuId: string;
    onActionComplete: () => void;
  }): React.ReactElement | null {
  if (!menuOpen) return null;

  return (
    <div
      id={menuId}
      role="group"
      aria-label={`Invoice #${invoice.number} actions menu`}
      className="mt-3 rounded-xl border border-line bg-paper p-2 shadow-sm"
    >
      <InvoiceRowActions
        invoice={invoice}
        variant="menu"
        actionBusy={actionBusy}
        downloadPending={downloadPending}
        paymentPending={paymentPending}
        onOpen={onOpen}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDownloadPdf={onDownloadPdf}
        onPaymentStatusChange={onPaymentStatusChange}
        onActionComplete={onActionComplete}
      />
    </div>
  );
}

function InvoiceRowActions({
  invoice,
  actionBusy,
  downloadPending,
  paymentPending,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
  onActionComplete,
  variant = 'row',
}: InvoiceRowActionsProps): React.ReactElement {
  const handleAction = (action: (invoice: InvoiceListItem) => void) => {
    action(invoice);
    onActionComplete?.();
  };
  const actions = buildInvoiceActionItems({
    invoice,
    actionBusy,
    downloadPending,
    paymentPending,
    handleAction,
    onOpen,
    onDuplicate,
    onDelete,
    onDownloadPdf,
    onPaymentStatusChange,
  });

  return (
    <div className={variant === 'menu' ? 'grid gap-2' : 'flex flex-wrap justify-end gap-2'}>
      {actions.map((action) => (
        <InvoiceActionButton key={action.ariaLabel} {...action} variant={variant} />
      ))}
    </div>
  );
}

function buildInvoiceActionItems({
  invoice,
  actionBusy,
  downloadPending,
  paymentPending,
  handleAction,
  onOpen,
  onDuplicate,
  onDelete,
  onDownloadPdf,
  onPaymentStatusChange,
}: InvoiceActionHandlers &
  InvoiceActionState & {
    invoice: InvoiceListItem;
    handleAction: (action: (invoice: InvoiceListItem) => void) => void;
  }): InvoiceActionItem[] {
  const actions: InvoiceActionItem[] = [
    actionItem('Open', `Open invoice #${invoice.number}`, () => handleAction(onOpen)),
    actionItem(
      'Duplicate',
      `Duplicate invoice #${invoice.number}`,
      () => handleAction(onDuplicate),
      {
        disabled: actionBusy,
      },
    ),
    actionItem(
      invoice.isPaid ? 'Mark unpaid' : 'Mark paid',
      `${invoice.isPaid ? 'Mark unpaid' : 'Mark paid'} invoice #${invoice.number}`,
      () => onPaymentStatusChange(invoice, !invoice.isPaid),
      { disabled: paymentPending },
    ),
    actionItem(
      downloadPending ? 'Preparing PDF...' : 'Download PDF',
      `Download PDF invoice #${invoice.number}`,
      () => handleAction(onDownloadPdf),
      { disabled: downloadPending },
    ),
    actionItem('Delete', `Delete invoice #${invoice.number}`, () => handleAction(onDelete), {
      disabled: actionBusy || invoice.isArchive,
      tone: 'danger',
    }),
  ];

  return actions;
}

function actionItem(
  label: string,
  ariaLabel: string,
  onClick: () => void,
  options: Pick<InvoiceActionItem, 'disabled' | 'tone'> = {},
): InvoiceActionItem {
  return { label, ariaLabel, onClick, ...options };
}

function InvoiceActionButton({
  label,
  ariaLabel,
  disabled,
  tone = 'neutral',
  variant,
  onClick,
}: InvoiceActionItem & {
  variant: 'row' | 'menu';
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={invoiceActionButtonClassName(tone, variant)}
    >
      {label}
    </button>
  );
}

function invoiceActionButtonClassName(tone: 'neutral' | 'danger', variant: 'row' | 'menu'): string {
  const layout = variant === 'menu' ? 'w-full justify-start' : 'justify-center';
  const toneClassName =
    tone === 'danger'
      ? 'border-clay/40 bg-paper text-clay hover:border-clay/70 hover:bg-clay/10'
      : 'border-line bg-paper text-ink hover:border-ochre/40 hover:bg-paper-deep';
  return `inline-flex min-h-11 items-center ${layout} rounded-full border px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] disabled:cursor-not-allowed disabled:opacity-60 transition ${toneClassName}`;
}

function ArchiveTag(): React.ReactElement {
  return (
    <span
      aria-label="Archive invoice"
      className="inline-flex items-center rounded-full border border-dashed border-ink-muted/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-muted"
    >
      Archive
    </span>
  );
}

function PaymentBadge({ paid }: { paid: boolean }): React.ReactElement {
  return (
    <span
      aria-label={`Payment status: ${paid ? 'Paid' : 'Unpaid'}`}
      className={
        paid
          ? 'inline-flex items-center rounded-full border border-sage/40 bg-sage/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-sage'
          : 'inline-flex items-center rounded-full border border-ochre/35 bg-ochre/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ochre-deep'
      }
    >
      {paid ? 'Paid' : 'Unpaid'}
    </span>
  );
}

function formatInvoiceDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year!, month! - 1, day)));
}
